import { Component } from '@theme/component';
import { mountJelly, destroyJelly } from './cosautil-jelly-exact.js?v=fa87cc497f280505db2d67e7a2aa9e36d3b97cc0d3e4f712e12b3aa5f23746c3';

const normalize = value => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase();
const visible = element => {
  if (!(element instanceof HTMLElement) || !element.isConnected || element.closest('[hidden],[inert],[aria-hidden="true"]') || ![...element.getClientRects()].some(rect => rect.width > 0 && rect.height > 0)) return false;
  for (let current = element; current; current = current.parentElement) {const css = getComputedStyle(current); if (css.display === 'none' || css.visibility === 'hidden' || Number(css.opacity) === 0) return false;}
  return true;
};
const money = value => {
  const text = String(value ?? '').replace(/\s/g, '');
  return /^\$(?:\d{1,3}(?:\.\d{3})+|\d+)(?:,00)?$/.test(text) ? Number(text.slice(1).replaceAll('.', '').replace(/,00$/, '')) : null;
};
const WAIT_MS = 12000;
const INITIAL_WAIT_MS = 45000;
const STABLE_MS = 100;

/** Observe this page's cart writes without inspecting or changing their data. */
export class CartActivityBarrier {
  constructor(scope = globalThis, {quietFrames = 2, timeoutMs = INITIAL_WAIT_MS} = {}) {
    this.scope = scope;
    this.quietFrames = quietFrames;
    this.timeoutMs = timeoutMs;
    this.active = true;
    this.pending = 0;
    this.epoch = 0;
    this.waiters = new Set();
    this.detachers = new Set();
    this.restore = [];
    this.setTimer = (scope.setTimeout || globalThis.setTimeout).bind(scope);
    this.clearTimer = (scope.clearTimeout || globalThis.clearTimeout).bind(scope);
    this.nextFrame = typeof scope.requestAnimationFrame === 'function' ? scope.requestAnimationFrame.bind(scope) : callback => this.setTimer(callback,16);
    this.cancelFrame = typeof scope.cancelAnimationFrame === 'function' ? scope.cancelAnimationFrame.bind(scope) : this.clearTimer;
    const barrier = this;
    if (typeof scope.fetch === 'function') {
      const original = scope.fetch;
      const wrapped = function(...args) {
        if (!barrier.active) return original.apply(this,args);
        const input = args[0];
        let matches = false;
        try {matches = barrier.isMutation(typeof input === 'string' ? input : input?.url || input?.href, args[1]?.method || input?.method || 'GET');} catch { /* Metadata inspection must never affect a request. */ }
        if (!matches) return original.apply(this,args);
        const finish = barrier.begin();
        let result;
        try {result = original.apply(this,args);} catch (error) {finish();throw error;}
        return Promise.resolve(result).then(response => {finish();return response;},error => {finish();throw error;});
      };
      scope.fetch = wrapped;
      this.restore.push(() => {if (scope.fetch === wrapped) scope.fetch = original;});
    }
    const prototype = scope.XMLHttpRequest?.prototype;
    if (prototype && typeof prototype.open === 'function' && typeof prototype.send === 'function') {
      const originalOpen = prototype.open;
      const originalSend = prototype.send;
      const requests = new WeakMap();
      const open = function(...args) {
        const result = originalOpen.apply(this,args);
        if (barrier.active) requests.set(this,barrier.isMutation(args[1],args[0]));
        return result;
      };
      const send = function(...args) {
        if (!barrier.active || !requests.get(this)) return originalSend.apply(this,args);
        const finish = barrier.begin();
        const xhr = this;
        const detach = () => {xhr.removeEventListener('loadend',settled);barrier.detachers.delete(detach);};
        const settled = () => {detach();finish();};
        xhr.addEventListener('loadend',settled,{once:true});
        barrier.detachers.add(detach);
        try {return originalSend.apply(xhr,args);} catch (error) {settled();throw error;}
      };
      prototype.open = open;prototype.send = send;
      this.restore.push(() => {if (prototype.open === open) prototype.open = originalOpen;if (prototype.send === send) prototype.send = originalSend;});
    }
  }
  isMutation(value, method) {
    try {
      const page = new URL(this.scope.location?.href || globalThis.location?.href);
      const url = new URL(value,page);
      return url.origin === page.origin && /\/cart\/(?:add|update|change|clear)\.js$/.test(url.pathname) && !['GET','HEAD'].includes(String(method || 'GET').toUpperCase());
    } catch {return false;}
  }
  begin() {
    if (!this.active) return () => {};
    this.pending++;this.epoch++;
    let finished = false;
    return () => {
      if (finished) return;
      finished = true;
      if (this.active) {this.pending--;this.epoch++;}
    };
  }
  wait({signal,timeoutMs = this.timeoutMs} = {}) {
    if (!this.active || signal?.aborted) return Promise.resolve(false);
    return new Promise(resolve => {
      const started = Date.now();
      let frame = null;
      let timer = null;
      let quiet = 0;
      let epoch = this.epoch;
      let finished = false;
      const finish = value => {
        if (finished) return;
        finished = true;
        if (frame !== null) this.cancelFrame(frame);
        if (timer !== null) this.clearTimer(timer);
        signal?.removeEventListener('abort',aborted);
        this.waiters.delete(cancel);
        resolve(value);
      };
      const aborted = () => finish(false);
      const cancel = () => finish(false);
      const poll = () => {
        frame = null;
        if (!this.active || signal?.aborted || Date.now() - started >= timeoutMs) {finish(false);return;}
        if (epoch !== this.epoch || this.pending) {epoch = this.epoch;quiet = 0;}
        if (!this.pending && ++quiet >= this.quietFrames) {finish(true);return;}
        frame = this.nextFrame(poll);
      };
      this.waiters.add(cancel);
      signal?.addEventListener('abort',aborted,{once:true});
      timer = this.setTimer(() => finish(false),timeoutMs);
      frame = this.nextFrame(poll);
    });
  }
  destroy() {
    if (!this.active) return;
    this.active = false;
    [...this.waiters].forEach(cancel => cancel());
    [...this.detachers].forEach(detach => detach());
    this.restore.forEach(restore => restore());
    this.pending = 0;
  }
}

/** Read the app's displayed quote; never calculate or write native order fields. */
function summaryAmounts(element) {
  const lines = element.innerText.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
  const amounts = {subtotal: null, shipping: null, discount: 0, total: null};
  const seen = new Set();
  for (let index = 0; index < lines.length; index++) {
    const label = normalize(lines[index]);
    const key = label === 'SUBTOTAL' ? 'subtotal' : /^(?:ENVIO|SHIPPING)$/.test(label) ? 'shipping'
      : /^(?:DISCOUNTS?|DESCUENTOS?)$/.test(label) ? 'discount' : label === 'TOTAL' ? 'total' : null;
    if (!key) continue;
    if (seen.has(key) || !lines[index + 1]) return null;
    seen.add(key);
    const value = lines[++index];
    const parsed = key === 'shipping' && /^(?:GRATIS|GRATUITO|FREE)$/i.test(value) ? 0
      : money(key === 'discount' ? value.replace(/^[-−]\s*/, '') : value);
    if (parsed === null) return null;
    amounts[key] = parsed;
  }
  return seen.has('subtotal') && seen.has('shipping') && seen.has('total') ? amounts : null;
}

/** Page-only bridge: the official app owns customer fields, validation and orders. */
export class JellyNativeBridge {
  constructor(root, profile, getSelection = null, options = {}) {
    this.root = root;
    this.profile = profile;
    this.abort = new AbortController();
    this.timer = null;
    this.session = null;
    this.portal = null;
    this.owner = null;
    this.syncing = false;
    this.generation = 0;
    this.openingSession = null;
    this.getSelection = getSelection;
    this.hasReadyQuote = false;
    this.openTask = null;
    this.statusTrigger = null;
    this.eventRoot = typeof window === 'undefined' ? document : window;
    this.cartActivity = options.cartActivity || (this.ready() ? new CartActivityBarrier(options.cartActivityScope || this.eventRoot) : null);
    this.eventRoot.addEventListener('click', this.onClick, {capture: true, signal: this.abort.signal});
    this.eventRoot.addEventListener('change', this.onChange, {capture: true, signal: this.abort.signal});
    this.eventRoot.addEventListener('submit', this.onSubmit, {capture: true, signal: this.abort.signal});
    if (!this.ready()) this.root.querySelectorAll('[data-jelly-buy]').forEach(button => {button.disabled = true;});
    this.observePresentation();
  }
  observePresentation() {
    // Legacy writes green title/button styles with inline !important. Remove
    // presentation only; the product-scoped stylesheet owns the first paint.
    const targets = '._rsi-modal-header-title,button[type="submit"],input[type="submit"],._rsi-modal-submit-button';
    const properties = ['background','background-color','color','border','border-color','border-radius','box-shadow','font','font-family','font-size','font-weight','line-height','text-transform','letter-spacing'];
    const sanitize = () => {
      if (!this.ready()) return;
      const portals = [...document.querySelectorAll(this.profile.nativeProfile.nativeContract.modalSelector)];
      if (portals.length !== 1) return;
      const portal = portals[0];
      if (portal.dataset.cuJellyCod && portal.dataset.cuJellyCod !== String(this.profile.nativeProfile.productId)) return;
      portal.querySelectorAll(targets).forEach(node => {
        properties.forEach(property => {if (node.style.getPropertyValue(property)) node.style.removeProperty(property);});
      });
      // A portal may be recreated by the app. Insert the informational block
      // only when the normal native ownership/context checks can identify it.
      if (this.session) this.context();
    };
    sanitize();
    if (typeof MutationObserver === 'undefined') return;
    this.presentationObserver = new MutationObserver(records => {
      if (records.some(record => record.type === 'childList' || record.target.matches?.(targets))) sanitize();
    });
    this.presentationObserver.observe(document.body, {childList:true,subtree:true,attributes:true,attributeFilter:['style']});
  }
  ready() {
    const native = this.profile.nativeProfile;
    return !this.abort.signal.aborted && native && (native.nativeReady === true || native.editorQa === true)
      && this.root.dataset.mode === 'native' && this.root.dataset.identityVerified === 'true' && this.root.dataset.variantsVerified === 'true'
      && this.root.dataset.productId === String(native.productId) && this.root.dataset.productHandle === native.handle
      && this.root.dataset.templateSuffix === this.profile.config.templateSuffix
      && Array.isArray(this.profile.variants) && this.profile.variants.some(variant => variant.available === true)
      && document.querySelectorAll('[data-jelly-root][data-mode="native"]').length === 1;
  }
  status(message) {
    const target = this.statusTrigger?.closest('.wc-cod-button-wrapper')?.querySelector('[data-jelly-status]') || this.root.querySelector('[data-jelly-status]');
    this.root.querySelectorAll('[data-jelly-status]').forEach(node => {node.textContent = node === target ? message : '';node.hidden = node !== target || !message;});
  }
  // Ownership is separate from permission to launch: an unavailable product or
  // editor review must still guard its own native controls.
  ownedLauncher(target = null) {
    const selector = this.profile.nativeProfile.nativeContract.launcherSelector;
    const candidates = [...this.root.querySelectorAll('[data-jelly-native-app]')].flatMap(app => [...app.querySelectorAll(selector)]);
    return (target ? candidates.find(owner => owner.contains(target)) : candidates[0]) || null;
  }
  ownsNativeForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form === this.session?.form) return true;
    const native = this.profile.nativeProfile;
    const contract = native.nativeContract;
    if (!form.matches(contract.formSelector)) return false;
    if (this.portal?.contains(form)) return true;
    const portal = form.closest(contract.modalSelector);
    if (!portal || (portal.dataset.cuJellyCod && portal.dataset.cuJellyCod !== String(native.productId))) return false;
    return Boolean(this.ownedLauncher()) && this.root.isConnected && this.root.dataset.mode === 'native'
      && this.root.dataset.productId === String(native.productId) && this.root.dataset.productHandle === native.handle
      && this.root.dataset.templateSuffix === this.profile.config.templateSuffix;
  }
  launcher(requireVisible = true) {
    if (!this.ready()) return null;
    const form = this.root.querySelector('form[data-jelly-native-form]');
    const app = this.root.querySelector('[data-jelly-native-app]');
    if (!(form instanceof HTMLFormElement) || !app || this.root.querySelectorAll('form[data-jelly-native-form]').length !== 1 || this.root.querySelectorAll('[data-jelly-native-app]').length !== 1) return null;
    if (form.method.toLowerCase() !== 'post' || new URL(form.action, location.href).pathname !== '/cart/add') return null;
    const selector = this.profile.nativeProfile.nativeContract.launcherSelector;
    const candidates = [...document.querySelectorAll(selector)];
    if (candidates.length !== 1) return null;
    const owner = candidates[0];
    if (!owner.isConnected || (requireVisible && !visible(owner)) || !app.contains(owner) || owner.closest('form') || owner.closest('button,input,a') || owner.hasAttribute('form')) return null;
    return owner;
  }
  prepareSession(selection, owner) {
    this.clearSession();
    const pack = this.profile.config.packages.find(candidate => candidate.quantity === Number(selection?.quantity));
    const items = Array.isArray(selection?.items) ? selection.items : selection ? [selection] : [];
    if (!owner || !pack || items.length !== pack.quantity) {this.status('Las opciones de pedido todavía no están disponibles.'); return false;}
    const mapped = items.map(item => item && this.profile.variants.find(variant => String(variant.id) === String(item.variantId) && normalize(variant.color) === normalize(item.color) && normalize(variant.size) === normalize(item.size) && variant.available));
    if (mapped.some(item => !item)) {this.status('Elige una combinación disponible para cada unidad.'); return false;}
    // The standard Shopify product form is the app's mounting hook. Its hidden
    // fields and change events must stay untouched: selections are reconciled
    // only through the official modal's visible package and per-unit controls.
    // The app's first cold request can take longer than later repricing. Once
    // one complete quote has settled, subsequent openings keep the short wait.
    this.session = {owner, generation: ++this.generation, pack, items: mapped, trigger: selection.element, deadline: Date.now() + (this.hasReadyQuote ? WAIT_MS : INITIAL_WAIT_MS), reconciled: false, mode: 'initial', signature: null, stableSince: 0};
    this.owner = owner;
    return true;
  }
  open = selection => {
    if (this.session && this.context()) return Promise.resolve(true);
    if (this.openTask) return this.openTask.promise;
    if (!this.ready()) {this.status('Las opciones de pedido todavía no están disponibles.');return Promise.resolve(false);}
    if (!this.cartActivity) this.cartActivity = new CartActivityBarrier(this.eventRoot);
    const task = {abort:new AbortController(),promise:null};
    this.openTask = task;
    this.statusTrigger = selection?.element || null;
    this.root.dataset.jellyCartState = 'waiting';
    this.status('Estamos preparando el formulario. Espera un momento.');
    const run = async () => {
      let idle = false;
      try {idle = await this.cartActivity.wait({signal:task.abort.signal});} catch { /* A failed observer cannot authorize a new native launch. */ }
      if (task.abort.signal.aborted || this.abort.signal.aborted) return false;
      if (!idle) {this.root.dataset.jellyCartState = 'timeout';this.status('El formulario todavía se está preparando. Cierra y vuelve a intentarlo.');return false;}
      const owner = this.launcher();
      if (!this.prepareSession(selection,owner)) {this.root.dataset.jellyCartState = 'blocked';return false;}
      this.root.dataset.jellyCartState = 'idle';
      this.openingSession = this.session;
      try {owner.click();} finally {this.openingSession = null;}
      if (task.abort.signal.aborted || this.abort.signal.aborted) return false;
      this.schedule(30);
      return true;
    };
    task.promise = run().catch(() => {
      if (!task.abort.signal.aborted && !this.abort.signal.aborted) {
        this.root.dataset.jellyCartState = 'blocked';
        this.status('No pudimos abrir el formulario. Vuelve a intentarlo.');
        this.clearSession();
      }
      return false;
    }).finally(() => {if (this.openTask === task) this.openTask = null;});
    return task.promise;
  };
  onClick = event => {
    if (!(event.target instanceof Element)) return;
    const ownedOwner = this.ownedLauncher(event.target);
    if (ownedOwner) {
      const owner = this.launcher();
      if (owner !== ownedOwner) {
        this.stopEvent(event);
        this.status('No pudimos confirmar las opciones del producto. Vuelve a intentarlo.');
        this.failSession('No pudimos confirmar las opciones del producto. Vuelve a intentarlo.');
        return;
      }
      if (this.openingSession !== this.session || this.session?.owner !== owner) {
        this.stopEvent(event);
        let selection = null;
        try {selection = this.getSelection?.();} catch { /* An unreadable page selection cannot authorize a native launch. */ }
        this.open(selection ? {...selection,element:owner} : null);
        return;
      }
      this.owner = owner;
      this.schedule(30);
      return;
    }
    const context = this.context();
    const control = event.target.closest('button,input');
    if (control?.type === 'submit' && this.ownsNativeForm(control.form)) {
      if (!this.canConfirm(context)) this.blockConfirmation(event, context);
      return;
    }
    if (!context) {
      if (this.session && this.portal?.contains(event.target) && control?.form?.matches(this.profile.nativeProfile.nativeContract.formSelector) && control.type === 'submit') this.blockConfirmation(event, null);
      return;
    }
    if (!context.portal.contains(event.target)) return;
    if (control && control.form === context.form && control.type === 'submit') {
      if (!this.canConfirm(context)) this.blockConfirmation(event, context);
      return;
    }
    if (!this.syncing && (event.target.closest(this.profile.nativeProfile.nativeContract.offerSelector)
      || event.target.closest('._rsi-modal-shipping-rates-item') || event.target.closest('select'))) this.nativeChanged(context);
  };
  onChange = event => {
    if (this.syncing || !(event.target instanceof Element)) return;
    const context = this.context();
    if (!context || !context.form.contains(event.target)) return;
    if (event.target.matches('select[data-offer-num-multi-sel][data-option-num]') || event.target.closest('._rsi-build-block-shipping-rates')) this.nativeChanged(context);
  };
  onSubmit = event => {
    if (!this.ownsNativeForm(event.target)) return;
    const context = this.context();
    if (!this.canConfirm(context)) this.blockConfirmation(event, context);
  };
  stopEvent(event) {
    event.preventDefault();
    event.stopImmediatePropagation?.();
    event.stopPropagation();
  }
  blockConfirmation(event, context) {
    this.stopEvent(event);
    if (!this.ready() || !this.session) {
      const message = this.profile.nativeProfile.editorQa ? 'Vista de revisión. No se enviarán pedidos.' : 'No pudimos confirmar las opciones del producto. Vuelve a intentarlo.';
      this.status(message);
      this.failSession(message);
      return;
    }
    if (this.profile.nativeProfile.editorQa) this.feedback(context, 'Vista de revisión. No se enviarán pedidos.');
    else this.feedback(context, 'Estamos actualizando tu selección. Espera un momento.');
    this.schedule(30);
  }
  nativeChanged(context) {
    if (!this.session || this.session.expired) return;
    this.session.mode = 'native';
    this.pending(context, true);
    this.schedule(30);
  }
  pending(context, freshWindow = false) {
    const session = this.session;
    if (!session) return;
    if (freshWindow || session.reconciled) session.deadline = Date.now() + WAIT_MS;
    session.reconciled = false;
    session.signature = null;
    session.stableSince = 0;
    if (context) context.portal.dataset.cuJellyState = 'pending';
    this.feedback(context, this.profile.nativeProfile.editorQa ? 'Vista de revisión. No se enviarán pedidos.' : 'Estamos actualizando tu selección. Espera un momento.');
  }
  feedback(context, message) {
    if (!context?.form) return;
    let node = this.session?.message;
    if (!node || !context.form.contains(node)) {
      node?.remove();
      node = document.createElement('p');
      node.dataset.cuJellyValidationStatus = String(this.profile.nativeProfile.productId);
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
      const submit = context.form.querySelector('button[type="submit"],input[type="submit"]');
      if (submit) submit.before(node); else context.form.append(node);
      if (this.session) this.session.message = node;
    }
    if (node.textContent !== message) node.textContent = message;
    node.hidden = !message;
  }
  context() {
    const session = this.session;
    if (!session || !this.ready() || this.launcher(false) !== session.owner) return null;
    const contract = this.profile.nativeProfile.nativeContract;
    const portals = [...document.querySelectorAll(contract.modalSelector)];
    if (portals.length !== 1) return null;
    const portal = portals[0];
    if (!visible(portal) || !portal.classList.contains(contract.openModalClass)) return null;
    if (portal.dataset.cuJellyCod && portal.dataset.cuJellyCod !== String(this.profile.nativeProfile.productId)) return null;
    this.portal = portal;
    portal.dataset.cuJellyCod = String(this.profile.nativeProfile.productId);
    document.body.dataset.cuJellyModal = String(this.profile.nativeProfile.productId);
    this.root.dataset.nativeOpen = 'true';
    const forms = [...portal.querySelectorAll(contract.formSelector)];
    if (forms.length !== 1 || !(forms[0] instanceof HTMLFormElement)) return null;
    this.session.form = forms[0];
    this.ensureShipping(portal, forms[0]);
    return {portal, form: forms[0]};
  }
  ensureShipping(portal, form) {
    const productId = String(this.profile.nativeProfile.productId);
    const header = form.querySelector('._rsi-modal-header');
    if (!header) return;
    const matches = [...portal.querySelectorAll('[data-cu-jelly-shipping]')]
      .filter(node => node.dataset.cuJellyShipping === productId);
    let block = matches.shift();
    matches.forEach(node => node.remove());
    if (!block) {
      block = document.createElement('section');
      block.className = 'jelly-exact-shipping';
      block.dataset.cuJellyShipping = productId;
      block.setAttribute('aria-label', 'Plazos de despacho y entrega');
      block.innerHTML = '<p class="jelly-exact-shipping__eyebrow">ENVÍO RÁPIDO</p><div class="jelly-exact-shipping__grid"><div class="jelly-exact-shipping__card"><strong class="jelly-exact-shipping__value">24 h</strong><span class="jelly-exact-shipping__label">Despacho</span><small class="jelly-exact-shipping__detail">Tras confirmar tu pedido</small></div><div class="jelly-exact-shipping__card"><strong class="jelly-exact-shipping__value">3 días</strong><span class="jelly-exact-shipping__label">Entrega</span><small class="jelly-exact-shipping__detail">Después del despacho</small></div></div><p class="jelly-exact-shipping__note">Despacho dentro de las 24 h de confirmar tu pedido. Los 3 días de entrega se cuentan desde el despacho.</p>';
    }
    if (header.nextElementSibling !== block) header.after(block);
  }
  canConfirm(context) {
    const session = this.session;
    if (!context || !session || session.expired || this.profile.nativeProfile.editorQa || !session.reconciled || !this.ready()) return false;
    const snapshot = this.readSelection(context);
    if (!snapshot || snapshot.signature !== session.signature) {this.pending(context, true); return false;}
    return true;
  };
  schedule(delay = 80) {
    if (this.timer || this.abort.signal.aborted) return;
    this.timer = setTimeout(() => {this.timer = null; this.inspect();}, delay);
  }
  inspect() {
    const session = this.session;
    if (!session) return;
    if (!this.ready() || this.launcher(false) !== session.owner) {this.failSession('No pudimos confirmar las opciones del producto. Vuelve a intentarlo.'); return;}
    const contract = this.profile.nativeProfile.nativeContract;
    const portals = [...document.querySelectorAll(contract.modalSelector)];
    const portal = portals.length === 1 ? portals[0] : null;
    if (!portal || !visible(portal) || !portal.classList.contains(contract.openModalClass)) {
      if (this.portal) {this.clearSession(true); return;}
      if (Date.now() > session.deadline) {this.failSession('No pudimos abrir el formulario. Vuelve a intentarlo.'); return;}
      this.schedule(); return;
    }
    const context = this.context();
    if (!context) {
      if (Date.now() > session.deadline) this.failSession('No pudimos confirmar el formulario. Vuelve a intentarlo.');
      else this.schedule();
      return;
    }
    if (session.expired) {this.schedule(160); return;}
    // Unverified editor QA remains open for DOM inspection, but can never submit.
    if (contract.verified !== true) {
      this.feedback(context, 'Vista de revisión. No se enviarán pedidos.');
      if (!this.profile.nativeProfile.editorQa && Date.now() > session.deadline) this.failSession('No pudimos confirmar las opciones del producto. Vuelve a intentarlo.');
      else this.schedule(160);
      return;
    }
    if (session.mode === 'initial' && !this.reconcile(context.portal, context.form, contract, session)) {
      this.feedback(context, 'Estamos actualizando tu selección. Espera un momento.');
      if (Date.now() > session.deadline) this.failSession('No pudimos conservar tu selección. Vuelve a intentarlo.');
      else this.schedule();
      return;
    }
    const snapshot = this.readSelection(context);
    if (!snapshot) {
      if (session.reconciled) this.pending(context, true);
      else this.feedback(context, 'Estamos actualizando tu selección. Espera un momento.');
      if (Date.now() > session.deadline) this.failSession('No pudimos confirmar el paquete y su total. Vuelve a intentarlo.');
      else this.schedule();
      return;
    }
    if (session.signature !== snapshot.signature) {
      if (session.reconciled) session.deadline = Date.now() + WAIT_MS;
      session.reconciled = false;
      session.signature = snapshot.signature;
      session.stableSince = Date.now();
      context.portal.dataset.cuJellyState = 'pending';
    }
    if (Date.now() - session.stableSince >= STABLE_MS) {
      if (!session.reconciled) {
        this.hasReadyQuote = true;
        session.deadline = Date.now() + WAIT_MS;
      }
      session.reconciled = true;
      session.mode = 'native';
      context.portal.dataset.cuJellyState = 'ready';
      this.feedback(context, this.profile.nativeProfile.editorQa ? 'Vista de revisión. No se enviarán pedidos.' : '');
      this.status('');
      this.publishSelection(snapshot.selection, session);
    }
    this.schedule(160);
  }
  reconcile(portal, form, contract, session) {
    if (!this.nativeIdentity(form)) return false;
    const cards = [...portal.querySelectorAll(contract.offerSelector)];
    const target = cards.filter(card => card.dataset.offerPos === String(contract.offerPositions[session.pack.quantity]));
    if (target.length !== 1 || !form.contains(target[0])) return false;
    const card = target[0];
    if (!card.classList.contains(contract.selectedOfferClass)) {
      const controls = [...card.querySelectorAll(contract.offerClickSelector)];
      if (controls.length === 1 && visible(controls[0])) {
        this.syncing = true;
        try {controls[0].click();} finally {this.syncing = false;}
      }
      return false;
    }
    const selects = [...card.querySelectorAll(contract.unitSelectSelector)];
    if (selects.length !== session.pack.quantity * 2) return false;
    for (let index = 0; index < session.items.length; index++) {
      for (const [key, number] of [['size', contract.sizeOptionNumber], ['color', contract.colorOptionNumber]]) {
        const matches = selects.filter(select => select.dataset.offerNumMultiSel === String(index + 1) && select.dataset.optionNum === String(number));
        if (matches.length !== 1 || !(matches[0] instanceof HTMLSelectElement) || matches[0].disabled || matches[0].form !== form || !visible(matches[0])) return false;
        const select = matches[0];
        const option = [...select.options].find(candidate => normalize(candidate.value) === normalize(session.items[index][key]) && !candidate.disabled);
        if (!option) return false;
        if (select.value !== option.value) {
          this.syncing = true;
          try {select.value = option.value; select.dispatchEvent(new Event('change', {bubbles: true}));} finally {this.syncing = false;}
          return false;
        }
      }
    }
    const totals = [...portal.querySelectorAll(contract.submitTotalSelector)].filter(visible);
    if (totals.length !== 1 || money(totals[0].innerText) !== session.pack.total) return false;
    return true;
  }
  readSelection({portal, form}) {
    const contract = this.profile.nativeProfile.nativeContract;
    if (contract.verified !== true || !this.nativeIdentity(form)) return null;
    const selected = [...portal.querySelectorAll(contract.offerSelector)].filter(card => card.classList.contains(contract.selectedOfferClass));
    if (selected.length !== 1 || !form.contains(selected[0])) return null;
    const pack = this.profile.config.packages.find(candidate => String(contract.offerPositions[candidate.quantity]) === selected[0].dataset.offerPos);
    if (!pack) return null;
    const selects = [...selected[0].querySelectorAll(contract.unitSelectSelector)];
    if (selects.length !== pack.quantity * 2) return null;
    const items = [];
    for (let index = 0; index < pack.quantity; index++) {
      const unit = {};
      for (const [key, number] of [['size',contract.sizeOptionNumber],['color',contract.colorOptionNumber]]) {
        const matches = selects.filter(select => select.dataset.offerNumMultiSel === String(index + 1) && select.dataset.optionNum === String(number));
        if (matches.length !== 1 || !(matches[0] instanceof HTMLSelectElement) || matches[0].disabled || matches[0].form !== form || !visible(matches[0])) return null;
        const option = [...matches[0].options].find(candidate => candidate.value === matches[0].value && !candidate.disabled);
        if (!option) return null;
        unit[key] = matches[0].value;
      }
      const variant = this.profile.variants.find(candidate => normalize(candidate.color) === normalize(unit.color) && normalize(candidate.size) === normalize(unit.size) && candidate.available);
      if (!variant) return null;
      items.push({color:variant.color,size:variant.size,variantId:variant.id});
    }
    const totals = [...portal.querySelectorAll(contract.submitTotalSelector)].filter(visible);
    const summaries = [...portal.querySelectorAll(contract.summarySelector || '._rsi-build-block-totals-summary')].filter(visible);
    if (totals.length !== 1 || summaries.length !== 1) return null;
    const quote = summaryAmounts(summaries[0]);
    const unitPrice = Number(this.profile.price || this.profile.config.unitPrice);
    const expectedDiscount = unitPrice * pack.quantity - Number(pack.total);
    const expectedShipping = Number(this.profile.config.standardShipping);
    if (!quote || expectedShipping !== 0 || Number(pack.discount ?? expectedDiscount) !== expectedDiscount
      || quote.subtotal !== unitPrice * pack.quantity || quote.discount !== expectedDiscount
      || quote.shipping !== expectedShipping || quote.total !== Number(pack.total) + expectedShipping
      || money(totals[0].innerText) !== quote.total || quote.subtotal - quote.discount + quote.shipping !== quote.total) return null;
    const selection = {productId:String(this.profile.nativeProfile.productId),handle:this.profile.nativeProfile.handle,quantity:pack.quantity,items,packageTotal:Number(pack.total),unitPrice};
    return {selection, signature:JSON.stringify(selection)};
  }
  publishSelection(selection, session) {
    const signature = JSON.stringify(selection);
    if (session.lastSelection === signature) return;
    session.lastSelection = signature;
    this.root.dispatchEvent(new CustomEvent('jelly:nativeselection',{detail:selection}));
  }
  nativeIdentity(form) {
    const links = [...form.querySelectorAll('a._rsi-modal-line-item-title[href]')];
    if (!links.length) return false;
    const ids = new Set(this.profile.variants.map(variant => String(variant.id)));
    try {
      const page = new URL(location.href);
      return links.every(link => {
        const url = new URL(link.getAttribute('href'),page);
        return url.origin === page.origin && url.pathname === '/products/' + this.profile.nativeProfile.handle && ids.has(url.searchParams.get('variant'));
      });
    } catch {return false;}
  }
  failSession(message) {
    const session = this.session;
    if (!session) return;
    session.reconciled = false;
    session.expired = true;
    this.status(message);
    const portal = this.portal;
    const selector = this.profile.nativeProfile.nativeContract.closeSelector || '#_rsi-cod-form-modal-close';
    const close = portal ? [...portal.querySelectorAll(selector)].filter(visible) : [];
    if (close.length === 1) close[0].click();
    if (!portal || !visible(portal) || !portal.classList.contains(this.profile.nativeProfile.nativeContract.openModalClass)) this.clearSession(true);
    else {this.feedback({portal, form:session.form}, 'No pudimos confirmar tu selección. Cierra el formulario y vuelve a intentarlo.'); this.schedule(160);}
  }
  clearSession(restoreFocus = false) {
    const trigger = this.session?.trigger;
    clearTimeout(this.timer); this.timer = null;
    this.session?.message?.remove();
    this.portal?.querySelectorAll('[data-cu-jelly-shipping]').forEach(node => {
      if (node.dataset.cuJellyShipping === String(this.profile.nativeProfile.productId)) node.remove();
    });
    if (this.portal?.dataset.cuJellyCod === String(this.profile.nativeProfile.productId)) {
      this.portal.removeAttribute('data-cu-jelly-cod');
      this.portal.removeAttribute('data-cu-jelly-state');
    }
    if (document.body.dataset.cuJellyModal === String(this.profile.nativeProfile.productId)) delete document.body.dataset.cuJellyModal;
    delete this.root.dataset.nativeOpen;
    this.portal = null; this.session = null; this.owner = null;
    if (restoreFocus && trigger instanceof HTMLElement && trigger.isConnected) trigger.focus({preventScroll: true});
  }
  destroy() {
    this.presentationObserver?.disconnect();
    this.openTask?.abort.abort();
    this.cartActivity?.destroy();
    const selector = this.profile.nativeProfile.nativeContract.closeSelector || '#_rsi-cod-form-modal-close';
    const close = this.portal ? [...this.portal.querySelectorAll(selector)].filter(visible) : [];
    if (close.length === 1) close[0].click();
    this.abort.abort();
    this.clearSession();
    delete this.root.dataset.jellyCartState;
  }
}

export class CosautilJelly extends Component {
  connectedCallback() {super.connectedCallback(); this.initialize();}
  updatedCallback() {super.updatedCallback(); this.initialize();}
  disconnectedCallback() {this.cleanup(); super.disconnectedCallback();}
  cleanup() {if (this.root) destroyJelly(this.root); this.bridge?.destroy(); this.bridge = null; this.root = null;}
  initialize() {
    this.cleanup();
    const roots = [...this.querySelectorAll('[data-jelly-root]')];
    const profiles = [...this.querySelectorAll('[data-jelly-profile]')];
    if (roots.length !== 1 || profiles.length !== 1) return;
    this.root = roots[0];
    try {
      const profile = JSON.parse(profiles[0].textContent);
      let mounted;
      this.bridge = new JellyNativeBridge(this.root, profile, () => mounted?.getSelection());
      mounted = mountJelly(this.root, {...profile, onBuy: this.bridge.open});
      if (!this.bridge.ready()) this.root.querySelectorAll('[data-jelly-buy]').forEach(button => {button.disabled = true;});
      if (!this.bridge.ready()) this.bridge.status('Las opciones de pedido todavía no están disponibles.');
    } catch {
      this.bridge?.destroy();
      destroyJelly(this.root);
      this.root.querySelectorAll('[data-jelly-buy]').forEach(button => {button.disabled = true;});
    }
  }
}
if (!customElements.get('cosautil-jelly-exact')) customElements.define('cosautil-jelly-exact', CosautilJelly);
